import { memo, useCallback, useMemo, useState } from 'react';
import { KeyboardArrowDown } from '@mui/icons-material';
import { czechFlag, englishFlag, slovakFlag } from 'assets/svgIcons';
import { Languages } from 'types/translator';
import { useTranslation } from 'react-i18next';
import { useStorage } from 'contexts/index';
import { STORAGE_KEYS } from 'types/storage';
import { useDispatch } from 'react-redux';
import { coreApi } from 'store/services/core';
import { flowApi } from 'store/services/flow';
import { documentStorageApi } from 'store/services/documentStorage';
import { heliosApi } from 'store/services/helios';
import { invokeRNMessage } from 'utils/common';
import { ReactNativeMessageType } from 'types';
import { FlagImage, LanguageBox, LanguageLabel, StyledMenu, StyledMenuItem } from './LanguageSelect.styles';

export const LanguageSelect = memo(() => {
  const { i18n } = useTranslation();
  const { setItem } = useStorage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dispatch = useDispatch();

  const resetCache = useCallback(() => {
    dispatch(coreApi.util.resetApiState());
    dispatch(flowApi.util.resetApiState());
    dispatch(documentStorageApi?.util.resetApiState());
    dispatch(heliosApi.util.resetApiState());
  }, [dispatch]);

  const languagesWithFlags = useMemo(
    () => [
      {
        id: 1,
        name: 'czech',
        flag: czechFlag,
        value: Languages.CS,
      },
      {
        id: 2,
        name: 'english',
        flag: englishFlag,
        value: Languages.EN,
      },
      {
        id: 3,
        name: 'slovak',
        flag: slovakFlag,
        value: Languages.SK,
      },
    ],
    []
  );

  const handleChangeLanguage = useCallback(
    async (value: Languages) => {
      await i18n.changeLanguage(value);
      setItem(STORAGE_KEYS.LANGUAGE, { language: value });
      invokeRNMessage(ReactNativeMessageType.ChangeLanguage, {
        value,
      });
      resetCache();
    },
    [i18n, resetCache, setItem]
  );

  return (
    <>
      <LanguageBox
        onClick={(e) => setAnchorEl(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setAnchorEl(e.currentTarget);
          }
        }}
        role="button"
        tabIndex={0}
        data-testid="admin-toolbar-language-select">
        <FlagImage src={languagesWithFlags.find((lang) => lang.value === i18n.language)?.flag} alt={i18n.language} />
        <LanguageLabel>{i18n.language}</LanguageLabel>
        <KeyboardArrowDown sx={{ color: 'text.secondary', fontSize: 18 }} />
      </LanguageBox>

      <StyledMenu
        anchorEl={anchorEl}
        disableScrollLock
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        elevation={0}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}>
        {languagesWithFlags.map((lang) => (
          <StyledMenuItem selected={lang.value === i18n.language} key={lang.value} onClick={() => void handleChangeLanguage(lang.value)}>
            <FlagImage src={lang.flag} alt={lang.name} />
            <span>{lang.name.charAt(0).toUpperCase() + lang.name.substring(1)}</span>
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </>
  );
});
